import axios from 'axios';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';


const sampleConcepts = {
  tax_expenses: ['IGI', 'IVA', 'DTA', 'Prevalidacion', 'Multa', 'C.C.', 'Recargos'],
  actual_expenses: ['Maniobras', 'Muellaje', 'Revalidacion BL', 'Carga y Descarga', 'Previo', 'Reconocimiento', 'Acarreo', 'Vacio', 'Demoras', 'Gastos Fijos', 'Almacenajes', 'Multas'],
  custom_brokerage_expenses: ['Honorarios', 'Validacion', 'Contraprestacion', 'COVE', 'IVA Servicios']
};

/**
 * Calls the backend to update the status of a PaymentAdvance or PaymentRequest.
 * 
 * @param {String} documentType - The type of document ('PaymentAdvance' or 'PaymentRequest').
 * @param {String} documentId - The ID of the document to update.
 * @param {String} newStatus - The new status for the document.
 * @returns {Promise} - The promise that resolves to the response of the status update request.
 */
export const updatePaymentStatus = async (documentType, documentId, newStatus) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/updateStatus`, {
      documentType,
      documentId,
      newStatus
    });
    console.log('Status update response:', response.data);
    return response.data; // Return the successful response data
  } catch (error) {
    console.error('Error updating status:', error.response?.data || error.message);
    throw error; // Rethrow the error for further handling
  }
};

export const generateAndUploadPDFWithDifferences = async (paymentAdvanceData, getPresignedUrlAndUpload, differences) => {
  const pdfDoc = await PDFDocument.create();
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const imageResponse = await axios.get('https://publicwdl.s3.us-east-2.amazonaws.com/wisdom.png', {
    responseType: 'arraybuffer',
    headers: {
      'Authorization': undefined,
    }
  });
  const logoImageBytes = new Uint8Array(imageResponse.data);
  const logoImage = await pdfDoc.embedPng(logoImageBytes);
  const { width, height } = logoImage.scale(0.5);

  const page = pdfDoc.addPage([595, 842]);
  const fontSize = 10;
  const scaleFactor = 0.15;
  const logoWidth = width * scaleFactor;
  const logoHeight = height * scaleFactor;
  const logoXPosition = 50;
  const logoYPosition = page.getHeight() - logoHeight - 50;
  let yCursor = page.getHeight() - logoHeight - 70;

  page.drawImage(logoImage, {
    x: logoXPosition,
    y: logoYPosition,
    width: logoWidth,
    height: logoHeight,
  });

  const drawText = (text, x, y, size = fontSize, font = normalFont, color = rgb(0, 0, 0)) => {
    page.drawText(text, { x, y, size, font, color });
  };

  const drawLine = (x1, y1, x2, y2) => {
    page.drawLine({
      start: { x: x1, y: y1 },
      end: { x: x2, y: y2 },
      thickness: 1,
      color: rgb(0.65, 0.65, 0.65),
    });
  };

  const drawSection = (sectionTitle, items, yCursor) => {
    yCursor -= 20;
    drawText(sectionTitle, 50, yCursor, 12, boldFont);
    drawLine(50, yCursor - 5, 545, yCursor - 5);
    yCursor -= 20;
    let totalAmount = 0;

    items.forEach(item => {
      const amount = parseFloat(item.amount);
      totalAmount += amount;
      const amountText = `$${amount.toFixed(2)}`;
      drawText(`${item.concept}:`, 50, yCursor, fontSize, boldFont);
      drawText(amountText, 150, yCursor, fontSize, normalFont);
      yCursor -= 15;
    });

    drawLine(50, yCursor - 5, 545, yCursor - 5);
    drawText(`Total `, 50, yCursor -= 20, fontSize, boldFont);
    drawText(`$${totalAmount.toFixed(2)}`, 150, yCursor, fontSize, boldFont);

    yCursor -= 15;
    return yCursor;
  };

  // Adjust the X position of the title to center it properly
  const title = 'SOLICITUD DE ANTICIPO';
  const titleWidth = boldFont.widthOfTextAtSize(title, 16);
  const titleXPosition = (page.getWidth() - titleWidth) / 2;

  // Draw the "SOLICITUD DE ANTICIPO" title centered
  drawText(title, titleXPosition, yCursor, 16, boldFont);

  // Draw the current date in the upper right corner
  const currentDate = new Date().toLocaleDateString('en-GB'); // Format: dd/MM/YYYY
  const dateWidth = normalFont.widthOfTextAtSize(currentDate, 10);
  const dateXPosition = page.getWidth() - dateWidth - 80; // Right align the date
  drawText(`DATE: ${currentDate}`, dateXPosition, yCursor, 10, boldFont); // Use boldFont if needed
  // Move yCursor down for the next line of text
  yCursor -= 30; // Adjust space as needed

  //ID for the PaymentAdvance
  drawText('REQUEST ID:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData._id}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 15;

  //Client information with conditional about the alternateCustomer
  const customerName = paymentAdvanceData.alternate_customer && paymentAdvanceData.alternate_customer.trim() !== ''
    ? paymentAdvanceData.alternate_customer
    : paymentAdvanceData.customer_data.customer_name;

  drawText('CLIENTE:', 50, yCursor, fontSize, boldFont);
  drawText(`${customerName}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('FECHA:', 50, yCursor, fontSize, boldFont);
  drawText(`${new Date().toLocaleDateString()}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('PATENTE:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.license}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('ADUANA:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.entry_point}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('PEDIMENTO:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.pedimento}`, 120, yCursor, fontSize, normalFont);
  yCursor -= 20;

  if (differences.hasDifferences) {
    differences.differences.forEach(difference => {
      // Map the section title to a more user-friendly format if needed
      const sectionTitles = {
        'tax_expenses': 'Tax Expenses',
        'actual_expenses': 'Actual Expenses',
        'custom_brokerage_expenses': 'Custom Brokerage Expenses'
      };

      const sectionTitle = sectionTitles[difference.field] || difference.field;
      const updatedItems = difference.updated;

      // Only draw sections for which there are updates
      if (updatedItems && updatedItems.length > 0) {
        yCursor = drawSection(sectionTitle, updatedItems, yCursor);
      }
    });
  }

  // Serialize and save PDF as before
  const pdfBytes = await pdfDoc.save();
  const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
  const pdfFile = new File([pdfBlob], "updated-payment-advance.pdf", { type: 'application/pdf' });

  // Upload the PDF and return metadata
  const fileInfo = await getPresignedUrlAndUpload(pdfFile);
  return fileInfo;
};


export const generateAndUploadPDF = async (paymentAdvanceData, getPresignedUrlAndUpload, differencesList) => {
  console.log(paymentAdvanceData);
  const pdfDoc = await PDFDocument.create();
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  // Fetch the image from the URL
  const imageResponse = await axios.get('https://publicwdl.s3.us-east-2.amazonaws.com/wisdom.png', {
    responseType: 'arraybuffer',
    headers: {
      // Explicitly override the Authorization header to undefined
      'Authorization': undefined,
    }
  });
  const logoImageBytes = new Uint8Array(imageResponse.data);

  // Embed the PNG image bytes
  const logoImage = await pdfDoc.embedPng(logoImageBytes);
  const { width, height } = logoImage.scale(0.5); // Scale the logo image

  const page = pdfDoc.addPage([595, 842]); // A4 size

  const fontSize = 10;

  // Scale down the logo image and place it at the top-left corner of the page
  const scaleFactor = 0.15; // Adjust scale factor as needed
  const logoWidth = width * scaleFactor;
  const logoHeight = height * scaleFactor;
  const logoXPosition = 50; // Adjust position as needed
  const logoYPosition = page.getHeight() - logoHeight - 50; // Adjust position as needed

  let yCursor = page.getHeight() - logoHeight - 70; // Start drawing text below the logo

  page.drawImage(logoImage, {
    x: logoXPosition,
    y: logoYPosition,
    width: logoWidth,
    height: logoHeight,
  });

  const drawText = (text, x, y, size = fontSize, font = normalFont, color = rgb(0, 0, 0)) => {
    page.drawText(text, { x, y, size, font, color });
  };

  const drawLine = (x1, y1, x2, y2) => {
    page.drawLine({
      start: { x: x1, y: y1 },
      end: { x: x2, y: y2 },
      thickness: 1,
      color: rgb(0.65, 0.65, 0.65),
    });
  };

  const drawSection = (sectionTitle, items, yCursor) => {
    yCursor -= 20; // Space before the section title
    drawText(sectionTitle, 50, yCursor, 12, boldFont);
    drawLine(50, yCursor - 5, 545, yCursor - 5); // Line under section title
    yCursor -= 20; // Space after the line
    let totalAmount = 0;

    items.forEach(item => {
      const amount = parseFloat(item.amount);
      totalAmount += amount;
      const amountText = `$${amount.toFixed(2)}`;
      drawText(`${item.concept}:`, 50, yCursor, fontSize, boldFont);
      drawText(amountText, 150, yCursor, fontSize, normalFont);
      yCursor -= 15; // Line item spacing
    });

    // Draw the total for the section
    drawLine(50, yCursor - 5, 545, yCursor - 5); // Line before total
    drawText(`Total `, 50, yCursor -= 20, fontSize, boldFont);
    drawText(`$${totalAmount.toFixed(2)}`, 150, yCursor, fontSize, boldFont);

    yCursor -= 15; // Space after total
    return yCursor;
  };

  // Adjust the X position of the title to center it properly
  const title = 'SOLICITUD DE ANTICIPO';
  const titleWidth = boldFont.widthOfTextAtSize(title, 16);
  const titleXPosition = (page.getWidth() - titleWidth) / 2;

  // Draw the "SOLICITUD DE ANTICIPO" title centered
  drawText(title, titleXPosition, yCursor, 16, boldFont);

  // Draw the current date in the upper right corner
  const currentDate = new Date().toLocaleDateString('en-GB'); // Format: dd/MM/YYYY
  const dateWidth = normalFont.widthOfTextAtSize(currentDate, 10);
  const dateXPosition = page.getWidth() - dateWidth - 80; // Right align the date
  drawText(`DATE: ${currentDate}`, dateXPosition, yCursor, 10, boldFont); // Use boldFont if needed
  // Move yCursor down for the next line of text
  yCursor -= 30; // Adjust space as needed

  //ID for the PaymentAdvance
  drawText('REQUEST ID:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData._id}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  //Client information with conditional about the alternateCustomer
  const customerName = paymentAdvanceData.alternate_customer && paymentAdvanceData.alternate_customer.trim() !== ''
    ? paymentAdvanceData.alternate_customer
    : paymentAdvanceData.customer_data.customer_name;

  // Extract and format container and bill of lading data
  console.log(paymentAdvanceData.container_data);
  
  const containerNumbers = paymentAdvanceData.container_data?.map(container => container.container_number.trim()).join(', ') ?? '';
  const bolNumbers = paymentAdvanceData.bill_of_lading_data?.map(bol => bol.bill_of_lading.trim()).join(', ') ?? '';
  // Merge the formatted strings, using ', ' if both are present
  const combinedReferences = [containerNumbers, bolNumbers].filter(ref => ref).join(', ');

  drawText('CLIENTE:', 50, yCursor, fontSize, boldFont);
  drawText(`${customerName}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('CUSTOMER REF.:', 50, yCursor, fontSize, boldFont);
  drawText(`${combinedReferences}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('FECHA:', 50, yCursor, fontSize, boldFont);
  drawText(`${new Date().toLocaleDateString()}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('PATENTE:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.license}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('ADUANA:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.entry_point}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 15;

  drawText('PEDIMENTO:', 50, yCursor, fontSize, boldFont);
  drawText(`${paymentAdvanceData.pedimento}`, 150, yCursor, fontSize, normalFont);
  yCursor -= 20;

  let grandTotalAmount = 0;

  const fieldToSectionTitleMap = {
    tax_expenses: 'Tax Expenses',
    custom_brokerage_expenses: 'Custom Brokerage Expenses',
    actual_expenses: 'Actual Expenses',
  };

  // Initialize sections either with the original data or empty if differences exist
  let sections = differencesList.hasDifferences ? {} : {
    'Tax Expenses': paymentAdvanceData.tax_expenses,
    'Actual Expenses': paymentAdvanceData.actual_expenses,
    'Custom Brokerage Expenses': paymentAdvanceData.custom_brokerage_expenses,
  };

  // If differences exist, use them to populate the sections
  if (differencesList.hasDifferences) {
    differencesList.differences.forEach(difference => {
      const sectionTitle = fieldToSectionTitleMap[difference.field];
      if (sectionTitle) {
        // Ensure the structure matches what drawSection expects
        sections[sectionTitle] = difference.updated.map(item => ({
          concept: item.concept,
          amount: item.amount,
        }));
      }
    });
  }


  Object.entries(sections).forEach(([sectionTitle, items]) => {
    const sectionTotal = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    grandTotalAmount += sectionTotal; // Accumulate section total to grand total
    yCursor = drawSection(sectionTitle, items, yCursor, sectionTotal);
  });

  // Draw grand total
  drawLine(50, yCursor - 5, 545, yCursor - 5); // Line before grand total
  drawText('Grand Total:', 50, yCursor -= 20, fontSize, boldFont);
  drawText(`$${grandTotalAmount.toFixed(2)}`, 150, yCursor, fontSize, boldFont);
  yCursor -= 50; // Space after grand total



  const parseSafeFloat = (value) => {
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? 0 : parsedValue;
  };

  const advanceBalance = parseSafeFloat(paymentAdvanceData.advance_balance).toFixed(2);

  // Display the Total Advance
  drawText('Total Advance:', 50, yCursor -= 20, fontSize, boldFont);
  drawText(`$${advanceBalance}`, 150, yCursor, fontSize, boldFont);
  yCursor -= 20; // Space after displaying Total Advance

  // Calculate and display Total to Deposit using the safe parsed advanceBalance
  const totalToDeposit = (grandTotalAmount - parseSafeFloat(advanceBalance)).toFixed(2);
  drawText('Total to Deposit:', 50, yCursor -= 20, fontSize, boldFont);
  drawText(`$${totalToDeposit}`, 150, yCursor, fontSize, boldFont);
  yCursor -= 20; // Space after displaying Total to Deposit

  // Footer text
  drawText('El depósito favor de realizarlo a la siguiente cuenta a nombre de Wisdom Forwarding, SC',
    50, yCursor -= 20, fontSize, normalFont);

  const startY = yCursor -= 15;
  const rectangleHeight = 20;
  const accountInfo = [
    { text: 'BBVA', xPosition: 50, width: 70 },
    { text: 'CUENTA: 0117769741', xPosition: 120, width: 160 },
    { text: 'CLABE: 012822001177697414', xPosition: 280, width: 265 }
  ];

  // Calculate the total width of all boxes to center them
  const totalWidth = accountInfo.reduce((sum, info) => sum + info.width, 0);
  const startX = (page.getWidth() - totalWidth) / 2;

  // Draw the footer boxes and text
  accountInfo.forEach((info, index) => {
    // Adjust xPosition based on cumulative widths of previous boxes
    const adjustedXPosition = accountInfo.slice(0, index).reduce((sum, i) => sum + i.width, startX);

    // Draw the account info text and rectangle
    drawText(info.text, adjustedXPosition + 5, startY - rectangleHeight + 5, fontSize, boldFont);
    page.drawRectangle({
      x: adjustedXPosition,
      y: startY - rectangleHeight,
      width: info.width,
      height: rectangleHeight,
      borderColor: rgb(0, 0, 0),
      borderWidth: 1
    });
  });

  // Draw the bottom line below the boxes
  page.drawLine({
    start: { x: startX, y: startY - rectangleHeight - 1 },
    end: { x: startX + totalWidth, y: startY - rectangleHeight - 1 },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  const pdfBytes = await pdfDoc.save();
  const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
  const pdfFile = new File([pdfBlob], "payment-advance.pdf", { type: 'application/pdf' });

  // Upload PDF and return metadata
  const fileInfo = await getPresignedUrlAndUpload(pdfFile);
  return fileInfo;
};


export const generateAndUploadPDFOld = async (paymentAdvanceData, getPresignedUrlAndUpload) => {
  const pdfDoc = await PDFDocument.create();

  // Load standard fonts
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const page = pdfDoc.addPage([595, 842]); // A4 size
  const fontSize = 10;
  const lineHeight = 14;
  let yCursor = 800; // Start from the top of the page
  const blueColor = rgb(0.16, 0.32, 0.75);

  // Setup function to draw text more easily
  const drawText = (text, x, y, size = fontSize, font = normalFont) => {
    page.drawText(text, { x, y, size, font, color: rgb(0, 0, 0) });
  };

  // Function to draw centered bold text
  const drawTitle = (text, y, font = boldFont) => {
    const textWidth = font.widthOfTextAtSize(text, 16);
    const pageWidth = 595; // A4 width in points
    const textX = (pageWidth / 2) - (textWidth / 2);
    drawText(text, textX, y, 16, font);
  };

  // Function to draw right-aligned bold text
  const drawRightText = (text, y, font = boldFont) => {
    const textWidth = font.widthOfTextAtSize(text, fontSize);
    const pageWidth = 595; // A4 width in points

    const textX = pageWidth - textWidth - 50;
    drawText(text, textX, y, fontSize, font);
  };

  const drawHeaderBackgroundWithTitle = (title, y, font, page) => {
    const titlePadding = 5;
    const textWidth = font.widthOfTextAtSize(title, fontSize);
    const rectHeight = fontSize + 2 * titlePadding;
    const pageWidth = 595; // A4 width in points

    // Correct method to draw a rectangle
    // Temporary workaround for the drawRectangle call
    page.drawRectangle({
      x: 0,
      y: 842 - 100 - rectHeight, // Adjusted for the static height of A4 paper
      width: 100,
      height: 50,
      color: rgb(0.95, 0.1, 0.1),
    });



    // Center the title text on the page
    const textX = (pageWidth / 2) - (textWidth / 2);
    page.drawText(title, {
      x: textX,
      y: y,
      size: fontSize,
      font: font,
      color: rgb(1, 1, 1), // White color for the text
    });
  };


  // Header title
  drawTitle('SOLICITUD DE ANTICIPO', yCursor);
  yCursor -= 2 * lineHeight;

  // Client information
  drawText(`CLIENTE: ${paymentAdvanceData.customer_data.customer_name}`, 50, yCursor);
  yCursor -= lineHeight;

  // Right-aligned date text
  drawRightText(`FECHA: ${new Date().toLocaleDateString()}`, yCursor);
  yCursor -= lineHeight;

  // Continue with client information
  drawText(`PATENTE: ${paymentAdvanceData.license}  ADUANA: ${paymentAdvanceData.entry_point}  PEDIMENTO: ${paymentAdvanceData.pedimento}`, 50, yCursor);
  yCursor -= lineHeight * 2; // Extra spacing before sections

  // Define sections titles
  const sections = {
    tax_expenses: 'IMPUESTOS ADUANALES',
    actual_expenses: 'GASTOS COMPROBADOS ESTIMADOS',
    custom_brokerage_expenses: 'SERVICIOS DE AGENCIA APROXIMADOS',
  };

  // Function to draw section items
  // Function to draw section items with check if the items array exists
  const drawSection = (title, items, concepts, startY, page) => {
    // Use a consistent rectHeight as defined in the previous function
    const titlePadding = 5;
    const rectHeight = fontSize + 2 * titlePadding; // Define the rectHeight here
    let currentY = startY;

    const pageWidth = 595; // A4 width in points

    // Draw blue header with title for the section
    drawHeaderBackgroundWithTitle(title, currentY, boldFont, pageWidth);
    currentY -= rectHeight + lineHeight; // Move cursor below the header

    // Center the expense section contents
    const maxConceptWidth = concepts.reduce((maxWidth, concept) => {
      const conceptWidth = normalFont.widthOfTextAtSize(`${concept}:`, fontSize);
      return conceptWidth > maxWidth ? conceptWidth : maxWidth;
    }, 0);
    const maxAmountWidth = 100; // Set a fixed width for the amount column
    const contentWidth = maxConceptWidth + maxAmountWidth;
    const startX = (pageWidth - contentWidth) / 2; // Calculate start X to center the content

    concepts.forEach(concept => {
      const item = items.find(i => i.concept === concept);
      const amount = item && !isNaN(parseFloat(item.amount)) ? `$${parseFloat(item.amount).toFixed(2)}` : '$0.00';
      drawText(`${concept}:`, startX, currentY); // Concept in normal font
      drawText(amount, startX + maxConceptWidth, currentY); // Value in normal font, aligned with the concept
      currentY -= lineHeight;
    });
    return currentY - lineHeight; // Extra spacing after section
  };

  // Draw each section
  Object.entries(sections).forEach(([key, title]) => {
    const items = Array.isArray(paymentAdvanceData[key]) ? paymentAdvanceData[key] : null;
    const concepts = sampleConcepts[key];
    // Pass the 'page' object to the drawSection function
    yCursor = drawSection(title, items, concepts, yCursor, page);
  });

  // Serialize and save PDF
  const pdfBytes = await pdfDoc.save();
  const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
  const pdfFile = new File([pdfBlob], "payment-advance.pdf", { type: 'application/pdf' });

  // Upload PDF and return metadata
  const fileInfo = await getPresignedUrlAndUpload(pdfFile);
  return fileInfo;
};